import { fromLocalInput, toLocalInput } from './parts';

/*
 * The batched production fields. Each save-bar section builds exactly the
 * body v2's section Save sent (ActivationOpsSections), and a save PUTs the
 * union of the dirty sections' bodies in one request.
 */

export interface PForm {
    name: string;
    eventName: string;
    eventType: string;
    description: string;
    billingEntity: string;
    guestCount: string;
    deliveryAt: string;
    showReadyAt: string;
    startAt: string;
    endAt: string;
    timezone: string;
    venueId: string;
    setupLocation: string;
    internetProvided: boolean;
    internetDetails: string;
    loadInInstructions: string;
    parkingInstructions: string;
    producerId: string;
    technicalLeadId: string;
    logisticsCoordinatorId: string;
    boothType: string;
    software: string;
    backdrop: string;
    printing: string;
    slideshow: string;
    attire: string;
    equipment: string;
    emailSubject: string;
    emailBody: string;
    textMessageBody: string;
    dropboxFolderUrl: string;
    notes: string;
}
export type PKey = keyof PForm;

const s = (v: any) => (v == null ? '' : String(v));

/* v2's startEdit defaults, per section */
export function fromRecord(r: any): PForm {
    const tz = r.timezone ?? 'America/New_York';
    return {
        name: s(r.name),
        eventName: s(r.eventName),
        eventType: r.eventType ?? 'other',
        description: s(r.description),
        billingEntity: s(r.billingEntity),
        guestCount: s(r.guestCount),
        deliveryAt: toLocalInput(r.deliveryAt, r.timezone),
        showReadyAt: toLocalInput(r.showReadyAt, r.timezone),
        startAt: toLocalInput(r.startAt, r.timezone),
        endAt: toLocalInput(r.endAt, r.timezone),
        timezone: tz,
        venueId: s(r.venueId),
        setupLocation: s(r.setupLocation),
        internetProvided: !!r.internetProvided,
        internetDetails: s(r.internetDetails),
        loadInInstructions: s(r.loadInInstructions),
        parkingInstructions: s(r.parkingInstructions),
        producerId: s(r.producerId),
        technicalLeadId: s(r.technicalLeadId),
        logisticsCoordinatorId: s(r.logisticsCoordinatorId),
        boothType: s(r.boothType),
        software: s(r.software),
        backdrop: s(r.backdrop),
        printing: s(r.printing),
        slideshow: s(r.slideshow),
        attire: s(r.attire),
        equipment: s(r.equipment),
        emailSubject: s(r.emailSubject),
        emailBody: s(r.emailBody),
        textMessageBody: s(r.textMessageBody),
        dropboxFolderUrl: s(r.dropboxFolderUrl),
        notes: s(r.notes),
    };
}

export const SECTION: Record<PKey, string> = {
    name: 'Details',
    eventName: 'Details',
    eventType: 'Details',
    description: 'Details',
    billingEntity: 'Details',
    guestCount: 'Details',
    deliveryAt: 'Schedule',
    showReadyAt: 'Schedule',
    startAt: 'Schedule',
    endAt: 'Schedule',
    timezone: 'Schedule',
    venueId: 'Venue',
    setupLocation: 'Venue',
    internetProvided: 'Venue',
    internetDetails: 'Venue',
    loadInInstructions: 'Venue',
    parkingInstructions: 'Venue',
    producerId: 'Leads',
    technicalLeadId: 'Leads',
    logisticsCoordinatorId: 'Leads',
    boothType: 'Booth',
    software: 'Booth',
    backdrop: 'Booth',
    printing: 'Booth',
    slideshow: 'Booth',
    attire: 'Booth',
    equipment: 'Booth',
    emailSubject: 'Comms & links',
    emailBody: 'Comms & links',
    textMessageBody: 'Comms & links',
    dropboxFolderUrl: 'Comms & links',
    notes: 'Comms & links',
};
export const ORDER = ['Details', 'Schedule', 'Venue', 'Leads', 'Booth', 'Comms & links'];

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function dirtySections(base: PForm, form: PForm): string[] {
    const set = new Set<string>();
    (Object.keys(SECTION) as PKey[]).forEach((k) => {
        if (!same(form[k], base[k])) set.add(SECTION[k]);
    });
    return ORDER.filter((x) => set.has(x));
}

const id = (v: string) => (v === '' ? null : +v);

/* one section's body, as v2 sent it */
export function sectionPayload(section: string, f: PForm, ctx: { venues: any[]; record: any }): Record<string, any> {
    switch (section) {
        case 'Details':
            return {
                name: f.name.trim(),
                eventName: f.eventName || null,
                eventType: f.eventType,
                description: f.description || null,
                billingEntity: f.billingEntity || null,
                guestCount: f.guestCount === '' ? null : +f.guestCount,
            };
        case 'Schedule':
            return {
                deliveryAt: fromLocalInput(f.deliveryAt, f.timezone),
                showReadyAt: fromLocalInput(f.showReadyAt, f.timezone),
                startAt: fromLocalInput(f.startAt, f.timezone),
                endAt: fromLocalInput(f.endAt, f.timezone),
                timezone: f.timezone || null,
            };
        case 'Venue': {
            const venue = ctx.venues.find((v) => +v.id === +f.venueId);
            /* v2 sent null when the venue list hadn't loaded, which wiped the event's location */
            const keep = f.venueId !== '' && +f.venueId === +ctx.record.venueId ? ctx.record.venueName : null;
            return {
                venueId: id(f.venueId),
                venueName: venue?.name ?? keep ?? null,
                setupLocation: f.setupLocation || null,
                internetProvided: f.internetProvided,
                internetDetails: f.internetDetails || null,
                loadInInstructions: f.loadInInstructions || null,
                parkingInstructions: f.parkingInstructions || null,
            };
        }
        case 'Leads':
            return {
                producerId: id(f.producerId),
                technicalLeadId: id(f.technicalLeadId),
                logisticsCoordinatorId: id(f.logisticsCoordinatorId),
            };
        case 'Booth':
            return {
                boothType: f.boothType || null,
                software: f.software || null,
                backdrop: f.backdrop || null,
                printing: f.printing || null,
                slideshow: f.slideshow || null,
                attire: f.attire || null,
                equipment: f.equipment || null,
            };
        case 'Comms & links':
            return {
                emailSubject: f.emailSubject || null,
                emailBody: f.emailBody || null,
                textMessageBody: f.textMessageBody || null,
                dropboxFolderUrl: f.dropboxFolderUrl || null,
                notes: f.notes || null,
            };
        default:
            return {};
    }
}

/* only sections being saved are checked */
export function validate(base: PForm, f: PForm, dirty: string[]): Partial<Record<PKey, string>> {
    const e: Partial<Record<PKey, string>> = {};
    if (dirty.includes('Details')) {
        if (!f.name.trim()) e.name = 'The event needs a name.';
        if (f.guestCount !== '' && !(Number.isInteger(+f.guestCount) && +f.guestCount >= 0)) e.guestCount = 'Use a whole number.';
    }
    if (dirty.includes('Schedule') && f.startAt && f.endAt && f.endAt < f.startAt) e.endAt = 'Ends before it starts.';
    if (dirty.includes('Comms & links') && f.dropboxFolderUrl !== base.dropboxFolderUrl && f.dropboxFolderUrl.trim() && !/^https?:\/\/\S+$/i.test(f.dropboxFolderUrl.trim()))
        e.dropboxFolderUrl = 'Start with https://';
    return e;
}
